import type { GetStaticProps, NextPage } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'

import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import { Icon } from '~/components/atoms/Icon/Icon'
import Link from '~/components/atoms/Link/Link'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'
import {
  SearchAnalystEventNames,
  SearchAnalystMethods,
} from '~/services/elastic/analyst'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import type { ArticlesPageData } from '~/utils/articles/getArticlesPageProps'
import { getArticlesPageProps } from '~/utils/articles/getArticlesPageProps'
import type { IPaginationContext } from '~/utils/doPagination'
import { addTranslations } from '~/utils/i18n'
import uniqueObjects from '~/utils/uniqueObjects'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'

import styles from '~/styles/pages.module.css'

// Dynamic imports
const ArticlesList = dynamic(
  () => import('~/components/blocks/ArticlesList/ArticlesList'),
  { ssr: true }
)
const Breadcrumb = dynamic(
  () => import('~/components/atoms/Breadcrumb/Breadcrumb'),
  { ssr: true }
)
const RelatedArticles = dynamic(
  () => import('~/components/blocks/RelatedArticles/RelatedArticles'),
  { ssr: true }
)
const MagazineHero = dynamic(
  () => import('~/components/blocks/MagazineHero/MagazineHero'),
  { ssr: true }
)
const MagazineDropdown = dynamic(
  () => import('~/components/atoms/MagazineDropdown/MagazineDropdown'),
  { ssr: true }
)
const FallbackPagination = dynamic(
  () => import('~/components/blocks/FallbackPagination/FallbackPagination'),
  { ssr: true }
)
const ArticlesFilter = dynamic(
  () => import('~/components/blocks/ArticlesFilter/ArticlesFilter'),
  { ssr: true }
)

const articlesListEvents = {
  article: {
    method: SearchAnalystMethods.convertedObjectIDs,
    name: SearchAnalystEventNames.articlesArticleClicked,
  },
  articleLarge: {
    method: SearchAnalystMethods.convertedObjectIDs,
    name: SearchAnalystEventNames.articlesArticleLargeClicked,
  },
  filter: {
    method: SearchAnalystMethods.convertedFilters,
    name: SearchAnalystEventNames.articlesArticleTypeClicked,
  },
}

export const ArticlesPage: NextPage<ArticlesPageData> = (
  props: ArticlesPageData
) => {
  const { query, asPath } = useRouter()
  const isLargeDevice = useMediaQuery(`(min-width: ${bp.bpLarge})`)
  const t = useTranslations('common')

  const {
    articles,
    breadcrumb,
    featuredArticles,
    settings,
    articleArtists,
    featuredCategories,
    otherFilterCats,
    indexSecondaryCategories,
    totalArticles,
    totalPerPage,
    theme,
    articlesTypeSlug,
    pageNumber = 1,
  } = props

  const nextPageNumber = pageNumber === 0 ? 2 : pageNumber + 1
  const featuredArticleIds =
    featuredArticles && featuredArticles?.map(article => article._id)

  const categoriesForFilters = uniqueObjects(
    [...featuredCategories, ...otherFilterCats, ...indexSecondaryCategories],
    'slug'
  )

  return (
    <>
      <Breadcrumb list={breadcrumb} />
      <MagazineHero />

      <div className={styles.articlesBg}>
        <MagazineDropdown
          featuredCategories={featuredCategories}
          articleArtists={articleArtists}
        />

        <RelatedArticles
          title={t('featuredReads')}
          articles={featuredArticles}
          hideViewAll
          theme={ComponentTheme.primary}
          disableFilters
          hideBrowse
          hideTitle={false}
          noPaddingBottom
          key={`featured-reads-${asPath}`}
        />

        <div id="all-articles">
          <Container gutters={gutterTypes.always}>
            <Fixture>
              {query.type && !isLargeDevice && (
                <div className={styles.articlesReturn}>
                  <Link href="/articles">
                    <span className={styles.articlesChevron}>
                      <Icon type="Chevron" height={10} width={10} />
                    </span>
                    <Text size={TextSizes.bodySml}>All articles</Text>
                  </Link>
                </div>
              )}
            </Fixture>
            <Text
              size={TextSizes.primaryTitleLrg}
              className={styles.latestArticlesTitle}
              Tag="h4"
            >
              {t('latestArticles')}
            </Text>
          </Container>
        </div>

        {categoriesForFilters && (
          <ArticlesFilter
            categoryList={categoriesForFilters}
            articleArtists={articleArtists}
            consolidateFilters={true}
            hideArtist={false}
            isArtistIndex={false}
            articlesTypeSlug={articlesTypeSlug}
            addSpacerTop={theme === 'dark'}
          />
        )}

        <ArticlesList
          articles={articles}
          magazineIndexBanner={settings.magazineIndexBanner}
          inListingBanners={settings.inListingBanners}
          myPortfolioBanner={settings.myPortfolioBanner}
          idFilterList={featuredArticleIds}
          pageNumber={pageNumber}
          articlesTypeSlug={articlesTypeSlug}
          searchInsightsEvents={articlesListEvents}
          useRowView={theme === 'dark'}
          grided={theme === 'dark'}
        />

        <FallbackPagination
          total={totalArticles}
          limit={totalPerPage}
          queryParams={{
            slug: 'articles',
            nextPage: nextPageNumber,
          }}
        />
      </div>
    </>
  )
}

export const getStaticPropsOriginal: GetStaticProps =
  async function getStaticPropsOriginal(context) {
    try {
      // Get SEO templates from centralized _app.tsx loading
      const seoTemplates = (context as any)?.pageProps?.seoTemplates
      const pageProps = await getArticlesPageProps(
        context as IPaginationContext,
        seoTemplates
      )

      return {
        props: {
          ...pageProps,
          notFound: false,
        },
      }
    } catch (error) {
      console.error('Error in getStaticProps:', error)
      return {
        notFound: true,
      }
    }
  }

// Add SSR translation support
export const getStaticProps = withLocalizedSettings(async context => {
  const result = await getStaticPropsOriginal(context)
  if ('notFound' in result || 'redirect' in result) {
    return result // Return notFound or redirect directly
  }
  return { props: await addTranslations(result.props, context) }
})
export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export default ArticlesPage
